import { useEffect, useState } from 'react';
import {
  apiRedacao,
  apiRelatorios,
  type AnotacaoDaSecao,
  type PecaDaRedacao,
  type PedidoDeRedacao,
  type PedidosDeRedacao,
} from '../lib/api';
import { usarEhMaster } from '../lib/papel.ts';
import { avisar, avisarErro } from './Avisos.tsx';
import { Anotacao } from './Anotacoes.tsx';
import { Icone } from './Icone.tsx';
import { Traco } from './ImoveisDoCaso';
import '../estilos/linha-rapida-2026.css';
import '../estilos/imoveis-2026.css';

/**
 * A máquina do SUMÁRIO EXECUTIVO — 08/09/2026.
 *
 * > *"O SUMÁRIO EXECUTIVO gere o prompt completo e perfeito (seguindo um padrão
 * > Kairós) […] eu copio esse prompt e colo no Claude ou GPT via chat, e o que
 * > gerar lá eu copio e colo no SUMÁRIO EXECUTIVO."* — Richard
 *
 * ## ⚠️ Por que o Hub não fala com a IA
 *
 * Não há chave, chamada nem integração. **O Hub monta um texto e devolve**; quem
 * o leva ao chat é uma pessoa, caso a caso, decidindo. É o que preserva o
 * essencial da regra do `CLAUDE.md` — o sistema não manda dado a serviço
 * nenhum — enquanto ele tem a redação que pediu.
 *
 * ⚠️ **Mas o dado sai da KAIRÓS, e a tela diz.** Foi levantado com a pesquisa à
 * vista: no plano de consumidor o treinamento vem **ligado por padrão** e a
 * conversa fica guardada. Ele decidiu assim mesmo — o aviso fica para que a
 * decisão continue consciente a cada uso, e não vire hábito silencioso.
 *
 * ## Os três passos, na ordem da decisão
 *
 * 1. **gerar** o prompt com o caso inteiro e as regras da casa;
 * 2. **copiar**, levar ao chat, trazer o texto de volta;
 * 3. ⭐ **conferir** — e é aqui que mora a acertabilidade: palavra proibida,
 *    conclusão de fraude, fonte citada, valor que não existe no caso. Tudo isso
 *    é determinístico e não depende do bom comportamento da IA.
 */
/**
 * ⭐ **Qual capítulo esta máquina está escrevendo — 09/09/2026.**
 *
 * ⚠️ **O mesmo componente serve às TRÊS seções** — sumário, hipóteses jurídicas
 * e recomendações. Os três passos são idênticos (gerar · levar · conferir), e
 * três telas iguais divergiriam na primeira correção feita só numa delas.
 * O que muda é **de onde vem o prompt** e o texto de apoio.
 */
export type PecaDaMaquina = 'sumario' | 'hipoteses' | 'recomendacoes' | 'consideracoes';

const APOIO: Record<PecaDaMaquina, string> = {
  sumario:
    'O Hub reúne tudo o que há neste caso — partes, bens, ônus, passivo, ' +
    'participações e o que foi consolidado — e monta o texto pronto para colar no ' +
    'Claude ou no GPT, já com as regras da KAIRÓS dentro.',
  hipoteses:
    'Ao material do caso o Hub acrescenta a linha do tempo e os sinais apurados ' +
    'sobre vínculos. ⛔ O prompt proíbe citar artigo de lei — nomear o instituto ' +
    'pode; o dispositivo é de quem assina com OAB.',
  recomendacoes:
    'Ao material do caso o Hub acrescenta a linha do tempo e os sinais apurados ' +
    'sobre vínculos. ⛔ O prompt proíbe prometer resultado — descreve o efeito ' +
    'esperado e o que ele depende.',
  /*
   * ⭐ **A peça que existe nos três níveis — 10/09/2026.** O apoio diz o que ela
   * NÃO faz, porque é justamente aí que ela se distingue das duas de cima: o
   * prompt manda concluir e proíbe interpretar.
   */
  consideracoes:
    'O Hub reúne o caso inteiro, a linha do tempo e os sinais apurados. ' +
    '⛔ O prompt manda concluir o que os fatos sustentam e proíbe interpretar: ' +
    'nada de nomear instituto jurídico nem sugerir providência — isso é dos ' +
    'capítulos seguintes, e só o Kairós os contrata.',
};

/**
 * ⭐ **De onde vêm o prompt e a conferência, quando não é uma seção — 09/09/2026.**
 *
 * ⚠️ **A quarta peça não é um capítulo: é um DOCUMENTO.** O resumo de um
 * contrato social em prosa passa pelos mesmos três passos, e a quarta tela
 * igual divergiria na primeira correção feita só numa delas. O que muda é de
 * onde sai o prompt e contra o que se confere — e é só isso que este par
 * carrega.
 */
export type FonteDaMaquina = {
  gerar: () => Promise<string>;
  conferir: (texto: string) => Promise<Array<{ gravidade: 'grave' | 'atencao'; aviso: string; trecho: string }>>;
  apoio: string;
  /** O que o botão de gravar diz. Ausente = "Gravar na seção". */
  rotuloGravar?: string;
};

/**
 * ⚠️ **Ou uma SEÇÃO do relatório, ou uma FONTE própria — nunca os dois.** A
 * união obriga quem chama a escolher, e é o compilador que cobra: com as duas
 * opcionais, um `relatorioId` esquecido viraria requisição para `/undefined/`
 * em tempo de execução.
 */
/**
 * ⭐⭐ **O CAMPO DE ESCRITA DENTRO DO PASSO 2 — decisão dele, 23/09/2026.**
 *
 * > *"O texto que eu colar no campo 'O texto que voltou', eu gostaria de ter a
 * > possibilidade de editar e utilizar as opções da barra de ferramentas nesse
 * > texto que colei. Assim, consigo grifar o que eu quiser, deixar em negrito
 * > e etc."* — Richard
 *
 * ⛔ **A faixa NÃO foi copiada, e isso é o principal.** Os botões dela agem
 * sobre a caixa do editor em que vivem; uma segunda faixa divergiria na
 * primeira correção feita só numa delas. Aqui entra o **mesmo componente**
 * `<Anotacao>` do "+ Acrescentar campo de escrita" — tabela, grifo, imagem,
 * PDF e conferência vêm junto porque é o mesmo código.
 *
 * ⚠️ **Só onde há seção.** Os usos com `fonte` própria (leitura de matrícula,
 * contrato social) não pertencem a um tópico do relatório e seguem com a caixa
 * de texto simples.
 */
export type CampoDoRetorno = {
  relatorioId: string;
  secaoId: string;
  anotacoes: AnotacaoDaSecao[];
  aoMudar: () => Promise<void> | void;
  /** Diz a quem chama qual campo o passo está mostrando, para não repeti-lo. */
  aoAbrirCampo: (anotacaoId: string | null, aberto: boolean) => void;
  /**
   * ⭐ **O título que o campo já nasce com**, quando quem chama tem um a dar.
   *
   * ⚠️ **Só o resumo de documento tem.** A decisão de 09/09/2026 é que o campo
   * leve o nome do arquivo — *"quem lê o relatório precisa saber de qual
   * documento o resumo saiu"*. Nos outros o título sairia impresso sem dizer
   * nada ao cliente, e por isso o campo nasce sem ele e sem o lugar de escrevê-lo.
   */
  titulo?: string;
};

/*
 * ⭐⭐ **O BOTÃO "GERAR TEXTO" — PRD §58, aprovado por ele em 19/10/2026.**
 *
 * > *"Lembrando que, esse botão 'gerar texto' é só para aqueles campos/tópicos que
 * > tem o botão de 'gerador de prompt'."* — Richard, 19/10/2026
 *
 * Por isso ele mora AQUI, na faixa do "Gerar o prompt", e em nenhum outro lugar: o
 * servidor monta o mesmo prompt, o redator o leva ao GPT, e o texto volta como
 * RASCUNHO conferido. Nada entra no tópico sem "Usar este texto", que cria um campo
 * de escrita NOVO no fim (nunca por cima). O caminho de copiar e colar fica ao lado.
 *
 * ⚠️ Os oito tópicos abrem a lista de pedidos ao mesmo tempo: uma ida só ao servidor
 * serve a todos (`listarJunto`).
 */
const listasEmCurso = new Map<string, Promise<PedidosDeRedacao>>();
function listarJunto(relatorioId: string): Promise<PedidosDeRedacao> {
  let lista = listasEmCurso.get(relatorioId);
  if (!lista) {
    lista = apiRedacao.listar(relatorioId).finally(() => {
      setTimeout(() => listasEmCurso.delete(relatorioId), 1_500);
    });
    listasEmCurso.set(relatorioId, lista);
  }
  return lista;
}

/** O que dizer quando não volta texto. Sempre com o caminho de copiar e colar ao lado. */
const POR_QUE_FALHOU: Record<NonNullable<PedidoDeRedacao['motivo']>, string> = {
  login:
    'O GPT do servidor está sem login. Use o prompt para copiar e colar, e renove o login ' +
    '(no terminal: bash /root/renovar-login-redator-kairos.sh).',
  cota: 'A cota da assinatura do GPT acabou por agora. Tente mais tarde ou use o prompt para copiar e colar.',
  tempo: 'O GPT passou de 5 minutos sem devolver o texto, e o pedido foi cancelado.',
  parado: 'O redator do servidor não pegou o pedido (está parado?). Use o prompt para copiar e colar.',
  outro: 'Não voltou texto. Tente de novo ou use o prompt para copiar e colar.',
};

/** Os achados do conferidor, como a tela sempre os mostrou. */
function ListaDeAchados({
  achados,
  doDocumento = false,
}: {
  achados: Array<{ gravidade: 'grave' | 'atencao'; aviso: string; trecho: string }>;
  /** ⭐ 19/10/2026: no resumo de documento a conferência é contra o ARQUIVO, e a frase diz isso. */
  doDocumento?: boolean;
}) {
  return (
    <div className="sumario-achados">
      {achados.length === 0 ? (
        <p className="imv-conferido">
          ✅ <strong>Nada a apontar.</strong>{' '}
          {doDocumento
            ? 'Nenhuma palavra proibida, e todo valor, CPF, CNPJ e data conferem com o documento.'
            : 'Nenhuma palavra proibida, nenhuma fonte citada, e todos os valores conferem com o caso.'}
        </p>
      ) : (
        <ul className="imv-achados">
          {achados.map((a, i) => (
            <li key={`${a.aviso}-${i}`} className={a.gravidade === 'grave' ? 'grave' : undefined}>
              <span>
                {a.gravidade === 'grave' ? '⛔ ' : '⚠️ '}
                {a.aviso}
              </span>
              <p>{a.trecho}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

type DaMaquina = {
  travado: boolean;
  aoAceitar: (texto: string) => Promise<void>;
  /** ⭐ Quando vem, o passo 2 é um campo de escrita com a faixa inteira. */
  campo?: CampoDoRetorno;
  /** ⛔ A seção já tem texto: o que se grava vai para o FIM, e o botão diz isso (15/09/2026). */
  jaEscrito?: boolean;
  /**
   * ⭐ PRD §58: o tópico do botão "Gerar texto". Sem `fonte`, é a própria `peca`; com
   * `fonte`, só quem passa (abertura, índice, linha do tempo). Contrato social e
   * matrícula não passam: ficam para depois do ensaio.
   */
  redacao?: PecaDaRedacao;
  /** ⭐ PRD §58.12: com `redacao="resumo-de-documento"`, o documento a resumir. */
  documentoId?: string;
} & (
  | { relatorioId: string; peca?: PecaDaMaquina; fonte?: undefined }
  | { relatorioId?: undefined; peca?: undefined; fonte: FonteDaMaquina }
);

export function SumarioDoRelatorio({
  relatorioId,
  peca = 'sumario',
  fonte,
  travado,
  aoAceitar,
  jaEscrito,
  campo,
  redacao,
  documentoId,
}: DaMaquina) {
  /** O campo que este passo está mostrando. Nulo antes de ele existir. */
  const [campoId, setCampoId] = useState<string | null>(null);
  const [criandoCampo, setCriandoCampo] = useState(false);
  const aberto = campo?.anotacoes.find((a) => a.id === campoId) ?? null;
  useEffect(() => {
    if (aberto) campo?.aoAbrirCampo(aberto.id, true);
    /* ⚠️ Ao sair da tela, o campo volta para a lista de baixo: some daqui e
       continuaria escondido lá, e o texto pareceria perdido. */
    return () => {
      if (aberto) campo?.aoAbrirCampo(aberto.id, false);
    };
  }, [aberto?.id]);

  async function abrirCampo(): Promise<void> {
    if (!campo || criandoCampo) return;
    setCriandoCampo(true);
    try {
      /* ⚠️ Sem título: o título do campo SAI IMPRESSO no documento, e
         "O texto que voltou" não é coisa que o cliente deva ler. O resumo de
         documento é a exceção, e traz o dele. */
      const nova = await apiRelatorios.criarAnotacao(
        campo.relatorioId,
        campo.secaoId,
        campo.titulo,
      );
      await campo.aoMudar();
      setCampoId(nova.id);
    } catch (e) {
      avisarErro(e);
    } finally {
      setCriandoCampo(false);
    }
  }
  const [prompt, setPrompt] = useState<string | null>(null);
  /* ⭐ O que anexar junto do prompt — 14/09/2026 (a íntegra do processo). */
  const [anexar, setAnexar] = useState<string | null>(null);
  const [gerando, setGerando] = useState(false);
  const [texto, setTexto] = useState('');
  const [achados, setAchados] = useState<
    Array<{ gravidade: 'grave' | 'atencao'; aviso: string; trecho: string }> | null
  >(null);
  const [conferindo, setConferindo] = useState(false);
  const ehMaster = usarEhMaster();

  /* ⭐ PRD §58 — o botão "Gerar texto". Só com campo de escrita: é nele que o texto entra. */
  const pecaDoGpt: PecaDaRedacao | null = redacao ?? (fonte ? null : peca);
  const relatorioDoGpt = campo?.relatorioId ?? relatorioId ?? null;
  const comGpt = Boolean(pecaDoGpt && relatorioDoGpt && campo && ehMaster);
  const [gptLigado, setGptLigado] = useState(false);
  const [pedido, setPedido] = useState<PedidoDeRedacao | null>(null);
  const [pedindo, setPedindo] = useState(false);
  const [decidindo, setDecidindo] = useState(false);
  /** O campo que nasceu do rascunho: ele aparece aqui, para a revisão. */
  const [doGpt, setDoGpt] = useState(false);

  async function atualizarPedido(junto = false): Promise<void> {
    if (!comGpt) return;
    try {
      const l = junto ? await listarJunto(relatorioDoGpt!) : await apiRedacao.listar(relatorioDoGpt!);
      setGptLigado(l.ligado);
      /* §58.12: o resumo é por documento; os tópicos não têm documento. */
      setPedido(l.pedidos.find((p) => p.peca === pecaDoGpt && (p.documentoId ?? null) === (documentoId ?? null)) ?? null);
    } catch {
      /* Sem a lista, o botão não aparece e o caminho de copiar e colar segue igual. */
      setGptLigado(false);
    }
  }
  useEffect(() => {
    void atualizarPedido(true);
  }, [relatorioDoGpt, pecaDoGpt, comGpt, documentoId]);
  /* Enquanto o GPT escreve, a tela pergunta de novo a cada 4 segundos. */
  const esperando = pedido?.situacao === 'na_fila' || pedido?.situacao === 'escrevendo';
  useEffect(() => {
    if (!esperando) return;
    const relogio = setInterval(() => void atualizarPedido(), 4_000);
    return () => clearInterval(relogio);
  }, [esperando, pedido?.id]);

  /* ⛔ 19/10/2026: `avisarErro` só lê a mensagem de um Error; recebendo o TEXTO, mostrava
     "Algo não funcionou." e escondia o motivo que o servidor deu (no teste dele, o aviso da
     linha do tempo sem fatos). Aqui, e no "Gerar o prompt" e no "Conferir", o texto vai direto. */
  async function gerarTexto(): Promise<void> {
    if (!comGpt || pedindo) return;
    setPedindo(true);
    try {
      setPedido(await apiRedacao.pedir(relatorioDoGpt!, pecaDoGpt!, documentoId));
      setDoGpt(false);
    } catch (e) {
      avisar(e instanceof Error ? e.message : 'Não consegui pedir o texto.', 'ruim');
    } finally {
      setPedindo(false);
    }
  }

  async function usarTexto(): Promise<void> {
    if (!pedido || !campo || decidindo) return;
    setDecidindo(true);
    try {
      const nova = await apiRedacao.usar(relatorioDoGpt!, pedido.id);
      await campo.aoMudar();
      setCampoId(nova.id);
      setDoGpt(true);
      setPedido(null);
      avisar('O texto está no tópico. Revise antes de seguir.');
    } catch (e) {
      avisar(e instanceof Error ? e.message : 'Não consegui pôr o texto no tópico.', 'ruim');
    } finally {
      setDecidindo(false);
    }
  }

  async function descartarTexto(): Promise<void> {
    if (!pedido || decidindo) return;
    setDecidindo(true);
    try {
      await apiRedacao.descartar(relatorioDoGpt!, pedido.id);
      setPedido(null);
    } catch (e) {
      avisar(e instanceof Error ? e.message : 'Não consegui descartar.', 'ruim');
    } finally {
      setDecidindo(false);
    }
  }

  async function gerar() {
    setGerando(true);
    try {
      if (fonte) {
        setPrompt(await fonte.gerar());
      } else {
        const r =
          peca === 'sumario'
            ? await apiRelatorios.promptDoSumario(relatorioId!)
            : await apiRelatorios.promptDaConclusao(relatorioId!, peca);
        setPrompt(r.prompt);
        setAnexar((r as { anexar?: string | null }).anexar ?? null);
      }
    } catch (e) {
      avisar(e instanceof Error ? e.message : 'Não consegui montar o prompt.', 'ruim');
    } finally {
      setGerando(false);
    }
  }

  async function conferir() {
    if (!texto.trim()) return;
    setConferindo(true);
    try {
      const achados = fonte
        ? await fonte.conferir(texto)
        : (await apiRelatorios.conferirSumario(relatorioId!, texto)).achados;
      setAchados(achados);
      if (achados.length === 0) avisar('Conferido: nada a apontar.');
    } catch (e) {
      avisar(e instanceof Error ? e.message : 'Não consegui conferir o texto.', 'ruim');
    } finally {
      setConferindo(false);
    }
  }

  /*
   * ⚠️ **Copiar tem plano B.** `navigator.clipboard` exige HTTPS e permissão, e
   * falha calada em alguns navegadores. Falhando, a caixa é selecionada para
   * `Ctrl+C` — melhor que um botão que não faz nada e não diz.
   */
  async function copiar() {
    if (!prompt) return;
    try {
      await navigator.clipboard.writeText(prompt);
      avisar('Prompt copiado. Cole no Claude ou no GPT.');
    } catch {
      const caixa = document.getElementById(`prompt-${fonte ? 'documento' : peca}`) as HTMLTextAreaElement | null;
      caixa?.select();
      avisar('Selecionei o texto — use Ctrl+C para copiar.');
    }
  }

  const graves = (achados ?? []).filter((a) => a.gravidade === 'grave').length;

  /*
   * ⛔ **A máquina é do master — 19/09/2026.** As rotas (prompt e conferência)
   * são `SO_MASTER` de propósito: o prompt reúne o caso inteiro, o conteúdo
   * mais sensível que o Hub devolve de uma vez. A faixa aparecia para todos, e
   * o colaborador clicava e recebia erro. Como no resto da casa, ele não tem o
   * botão e pede. Escrever na seção à mão continua sendo dele.
   */
  if (!ehMaster) return null;

  return (
    /* ⭐ No desenho de Imóveis desde 19/09/2026 (PRD §46.8): a faixa com o que o
       prompt carrega e o botão; gerado, os dois passos num painel. Os textos,
       os botões e as rotas são os de antes. */
    <div className="sumario-maquina">
      {!prompt && comGpt && gptLigado && !travado && pedido ? (
        <PainelDoGpt
          pedido={pedido}
          ocupado={decidindo || pedindo}
          aoUsar={() => void usarTexto()}
          aoDeNovo={() => void gerarTexto()}
          aoDescartar={() => void descartarTexto()}
          aoSoOPrompt={() => {
            void descartarTexto();
            void gerar();
          }}
        />
      ) : !prompt && comGpt && gptLigado && !travado && doGpt && aberto ? (
        /* ⭐ O campo que nasceu do rascunho, aqui mesmo, para a revisão. */
        <div className="imv-painel imv-maquina">
          <div className="sumario-passo">
            <p className="imv-passo-cab">
              <span className="imv-n">✓</span> O texto do GPT está no tópico. Revise, edite e grife aqui.
            </p>
            <Anotacao
              relatorioId={campo!.relatorioId}
              anotacao={aberto}
              travado={travado}
              semTitulo={!campo!.titulo}
              podeApagar
              aoMudar={async () => {
                await campo!.aoMudar();
              }}
            />
          </div>
        </div>
      ) : !prompt && comGpt && gptLigado && !travado ? (
        <div className="imv-faixa-acao">
          <span className="imv-faixa-ic" aria-hidden="true">
            <Traco d={['M4 5h16v11H8l-4 4z', 'M8 9h8', 'M8 12.5h5']} />
          </span>
          <div className="imv-faixa-txt">
            <span className="imv-rot">Texto deste tópico com o GPT</span>
            <p>{fonte?.apoio ?? APOIO[peca]}</p>
          </div>
          <div className="imv-faixa-botoes">
            <button type="button" className="imv-bt primario" disabled={pedindo} onClick={() => void gerarTexto()}>
              {pedindo ? 'Reunindo o caso…' : 'Gerar texto'}
            </button>
            <button type="button" className="imv-bt fantasma p" disabled={gerando} onClick={() => void gerar()}>
              {gerando ? 'Reunindo o caso…' : 'Só o prompt (copiar e colar)'}
            </button>
          </div>
        </div>
      ) : !prompt ? (
        <div className="imv-faixa-acao">
          <span className="imv-faixa-ic" aria-hidden="true">
            <Traco d={['M4 5h16v11H8l-4 4z', 'M8 9h8', 'M8 12.5h5']} />
          </span>
          <div className="imv-faixa-txt">
            <span className="imv-rot">Prompt para o Claude ou o GPT</span>
            <p>{fonte?.apoio ?? APOIO[peca]}</p>
          </div>
          <button
            type="button"
            className="imv-bt"
            disabled={gerando || travado}
            onClick={() => void gerar()}
          >
            {gerando ? 'Reunindo o caso…' : 'Gerar o prompt'}
          </button>
        </div>
      ) : (
        <div className="imv-painel imv-maquina">
          <div className="sumario-passo">
            <p className="imv-passo-cab">
              <span className="imv-n">1</span> O prompt
            </p>
            {/* ⭐⭐ O processo do cliente vai JUNTO — 14/09/2026. O prompt manda
                ler os autos; sem o PDF na conversa, o modelo declara que não os
                examinou. O aviso vem antes da caixa, para não ser esquecido. */}
            {anexar && <p className="aviso-atencao">📎 {anexar}</p>}
            <span className="imv-in lr-texto">
              <textarea id={`prompt-${fonte ? 'documento' : peca}`} readOnly rows={10} value={prompt} />
            </span>
            <div className="sumario-acoes">
              <button type="button" className="imv-bt primario" onClick={() => void copiar()}>
                <Icone nome="copiar" tamanho={14} /> Copiar o prompt
              </button>
              <button type="button" className="imv-bt fantasma p" onClick={() => void gerar()}>
                Refazer com o caso atual
              </button>
            </div>
            {/*
              ⛔ **O aviso que não sai daqui.** O dado do investigado sai da
              KAIRÓS ao ser colado, e no plano de consumidor o treinamento vem
              ligado por padrão. Ele decidiu assim — o aviso mantém a decisão
              consciente a cada uso.
            */}
            {/* ⭐ 09/10/2026, terceira revisão dos prompts: o caminho é o GPT, e o
                Chat temporário dele é o que mais protege o dado sem mudar o
                trabalho — fora do histórico, fora da memória e fora do treino. */}
            <div className="imv-travas">
              <p>
                ⚠️ Este texto leva <strong>nomes, documentos e patrimônio</strong> das partes
                investigadas. Ao colar num chat, esse dado sai da KAIRÓS. No GPT, use o{' '}
                <strong>Chat temporário</strong>: a conversa não fica no histórico, não alimenta a
                memória e não é usada para treinar o modelo (fica guardada por até 30 dias, só para
                segurança). Fora dele, nos planos pessoais o treinamento vem ligado por padrão: vale
                desligá-lo em Configurações, Controles de dados.
              </p>
            </div>
          </div>

          <div className="sumario-passo">
            <p className="imv-passo-cab">
              <span className="imv-n">2</span> O texto que voltou
            </p>
            {campo ? (
              /*
               * ⭐ **A faixa inteira, porque é o MESMO editor.** Cole aqui e
               * grife, marque em negrito, monte tabela ou anexe um print — o
               * que este campo aceita é o que qualquer campo de escrita
               * aceita, e a conferência roda ao gravar como em todos eles.
               */
              aberto ? (
                <Anotacao
                  relatorioId={campo.relatorioId}
                  anotacao={aberto}
                  travado={travado}
                  semTitulo={!campo.titulo}
                  podeApagar
                  aoMudar={async () => {
                    await campo.aoMudar();
                  }}
                />
              ) : (
                <div className="sumario-abrir-campo">
                  <button
                    type="button"
                    className="imv-bt primario"
                    disabled={travado || criandoCampo}
                    onClick={() => void abrirCampo()}
                  >
                    {criandoCampo ? 'Abrindo…' : 'Abrir o campo para colar'}
                  </button>
                  <p className="lr-ajuda">
                    O campo tem a faixa de formatação inteira — negrito, grifo, marcador,
                    tabela e anexo —, e o que você escrever nele já é texto deste tópico.
                  </p>
                </div>
              )
            ) : (
              <span className="imv-in lr-texto">
                <textarea
                  rows={10}
                  value={texto}
                  disabled={travado}
                  placeholder="Cole aqui o texto que a IA escreveu."
                  onChange={(e) => {
                    setTexto(e.target.value);
                    /* ⚠️ O resultado velho some ao mudar o texto: conferência que
                       sobrevive à edição é conferência que mente. */
                    setAchados(null);
                  }}
                />
              </span>
            )}
            {/*
              ⛔ **Com o campo de escrita, estes botões somem — e é a mudança
              que ele aprovou em 23/09/2026.** O campo grava sozinho e confere
              ao gravar, como todo campo de escrita; "Gravar na seção"
              duplicaria o mesmo texto em dois lugares do relatório.
            */}
            {!campo && (
            <div className="sumario-acoes">
              <button
                type="button"
                className="imv-bt"
                disabled={conferindo || !texto.trim()}
                onClick={() => void conferir()}
              >
                {conferindo ? 'Conferindo…' : 'Conferir'}
              </button>
              {/*
                ⚠️ **Gravar exige ter conferido**, e o botão diz isso. Não é
                trava de segurança — é o passo que ele mesmo pediu ao escolher a
                conferência. Com achados graves, o rótulo muda para deixar claro
                o que está sendo aceito.
              */}
              <button
                type="button"
                className="imv-bt primario"
                disabled={travado || !texto.trim() || achados === null}
                title={
                  achados === null ? 'Confira o texto antes de gravar.' : undefined
                }
                onClick={() => void aoAceitar(texto)}
              >
                {graves > 0
                  ? `Gravar mesmo assim (${graves})`
                  : (fonte?.rotuloGravar ?? (jaEscrito ? 'Acrescentar ao fim da seção' : 'Gravar na seção'))}
              </button>
            </div>
            )}
          </div>

          {achados !== null && <ListaDeAchados achados={achados} doDocumento={redacao === 'resumo-de-documento'} />}
        </div>
      )}
    </div>
  );
}

/**
 * ⭐ O painel do pedido ao GPT (PRD §58.2): escrevendo, rascunho na mesa, ou falha.
 *
 * ⛔ **O rascunho NÃO está no relatório**, e a tela diz isso em cima dele. Só "Usar este
 * texto" o leva ao tópico, num campo novo no fim. Com achado grave, o botão diz o que
 * está sendo aceito, como o "Gravar mesmo assim" do caminho de copiar e colar.
 */
function PainelDoGpt({
  pedido,
  ocupado,
  aoUsar,
  aoDeNovo,
  aoDescartar,
  aoSoOPrompt,
}: {
  pedido: PedidoDeRedacao;
  ocupado: boolean;
  aoUsar: () => void;
  aoDeNovo: () => void;
  aoDescartar: () => void;
  aoSoOPrompt: () => void;
}) {
  if (pedido.situacao === 'na_fila' || pedido.situacao === 'escrevendo') {
    return (
      <div className="imv-faixa-acao" aria-live="polite">
        <span className="imv-faixa-ic" aria-hidden="true">
          <Traco d={['M12 6v6l4 2', 'M12 3a9 9 0 1 0 0 18a9 9 0 1 0 0-18']} />
        </span>
        <div className="imv-faixa-txt">
          <span className="imv-rot">O GPT está escrevendo</span>
          <p>
            {pedido.situacao === 'na_fila' && pedido.naFrente > 0
              ? `Na fila: ${pedido.naFrente === 1 ? '1 pedido antes deste' : `${pedido.naFrente} pedidos antes deste`}. `
              : ''}
            Leva de 1 a 3 minutos. Pode trabalhar em outro tópico; o rascunho aparece aqui.
            {pedido.documentoEntrou !== null && ` Entrou ${pedido.documentoEntrou}% do documento no pedido.`}
          </p>
        </div>
      </div>
    );
  }

  if (pedido.situacao === 'falhou') {
    return (
      <div className="imv-painel imv-maquina">
        <div className="sumario-passo">
          <p className="imv-passo-cab">
            <span className="imv-n">!</span> Não voltou texto
          </p>
          <p className="aviso-atencao">{POR_QUE_FALHOU[pedido.motivo ?? 'outro']}</p>
          <div className="sumario-acoes">
            <button type="button" className="imv-bt primario" disabled={ocupado} onClick={aoSoOPrompt}>
              Só o prompt (copiar e colar)
            </button>
            <button type="button" className="imv-bt" disabled={ocupado} onClick={aoDeNovo}>
              Tentar de novo
            </button>
            <button type="button" className="imv-bt fantasma p" disabled={ocupado} onClick={aoDescartar}>
              Fechar
            </button>
          </div>
        </div>
      </div>
    );
  }

  const achados = pedido.achados ?? [];
  const graves = achados.filter((a) => a.gravidade === 'grave').length;
  return (
    <div className="imv-painel imv-maquina">
      <div className="sumario-passo">
        <p className="imv-passo-cab">
          <span className="imv-n">1</span> Rascunho do GPT · ainda não está no relatório
        </p>
        {/* ⚠️ §58.12: documento longo é cortado no pedido, como no copiar e colar; a tela diz quanto entrou. */}
        {pedido.documentoEntrou !== null && (
          <p className="aviso-atencao">
            O documento não coube inteiro: entrou {pedido.documentoEntrou}% dele no pedido, e o resumo cobre só essa parte.
          </p>
        )}
        <span className="imv-in lr-texto">
          <textarea readOnly rows={12} value={pedido.rascunho ?? ''} aria-label="Rascunho do GPT" />
        </span>
        <div className="imv-travas">
          <p>
            Escrito pelo GPT com os nomes, documentos e patrimônio das partes deste caso, pela
            assinatura, como no Chat temporário. Leia antes de usar: nada aqui entra no relatório
            sem o seu clique.
          </p>
        </div>
      </div>
      <div className="sumario-passo">
        <p className="imv-passo-cab">
          <span className="imv-n">2</span> A conferência do Hub
        </p>
        <ListaDeAchados achados={achados} doDocumento={pedido.documentoId !== null} />
        <div className="sumario-acoes">
          <button type="button" className="imv-bt primario" disabled={ocupado} onClick={aoUsar}>
            {graves > 0 ? `Usar mesmo assim (${graves})` : 'Usar este texto'}
          </button>
          <button type="button" className="imv-bt" disabled={ocupado} onClick={aoDeNovo}>
            Escrever de novo
          </button>
          <button type="button" className="imv-bt fantasma p" disabled={ocupado} onClick={aoDescartar}>
            Descartar
          </button>
        </div>
        <p className="lr-ajuda">
          "Usar este texto" cria um campo de escrita no fim do tópico, com a faixa de formatação
          inteira; o que já está escrito não muda.
        </p>
      </div>
    </div>
  );
}
